import { supabase } from '../lib/supabase';

const PLAN_MAP = {
  monthly: { months: 1, label: 'Premium Monthly', amount: 2500 },
  yearly: { months: 12, label: 'Premium Yearly', amount: 5000 },
  lifetime: { months: null, label: 'Premium Lifetime', amount: 10000 },
};

/**
 * Admin subscription tools use the logged-in admin JWT + RLS (is_platform_admin).
 * No Edge Function required once migration `20260423160000_admin_subscription_tables_rls.sql` is applied.
 */
export const adminSubscriptionService = {
  async list(search = '') {
    const qSearch = String(search || '').trim();
    let q = supabase
      .from('user_profiles')
      .select(
        'id, email, username, display_name, subscription_status, subscription_plan, subscription_start_date, subscription_renewal_date, is_admin, created_at'
      )
      .order('created_at', { ascending: false })
      .limit(150);

    if (qSearch.length >= 2) {
      const esc = qSearch.replace(/%/g, '\\%').replace(/_/g, '\\_');
      q = q.or(`email.ilike.%${esc}%,username.ilike.%${esc}%,display_name.ilike.%${esc}%`);
    }

    const { data: profiles, error: pErr } = await q;
    if (pErr) return { data: null, error: pErr };

    const ids = (profiles || []).map((p) => p.id).filter(Boolean);
    const subs = {};
    const pendings = {};

    if (ids.length) {
      const { data: subRows, error: sErr } = await supabase
        .from('stripe_subscriptions')
        .select(
          'user_id, status, plan_id, plan_name, amount, stripe_subscription_id, current_period_end, metadata, created_at'
        )
        .in('user_id', ids)
        .order('created_at', { ascending: false });

      if (sErr) return { data: null, error: sErr };

      for (const row of subRows || []) {
        const uid = row.user_id;
        if (!subs[uid]) subs[uid] = [];
        subs[uid].push(row);
      }

      const { data: penRows, error: uErr } = await supabase
        .from('uropay_pending')
        .select('user_id, id, status, plan_id, amount, reference_number, created_at, completed_at')
        .in('user_id', ids)
        .order('created_at', { ascending: false });

      if (uErr) return { data: null, error: uErr };

      for (const row of penRows || []) {
        const uid = row.user_id;
        if (!pendings[uid]) pendings[uid] = [];
        pendings[uid].push(row);
      }
    }

    const users = (profiles || []).map((p) => ({
      ...p,
      stripe_subscriptions: subs[p.id]?.[0] || null,
      uropay_pending_recent: (pendings[p.id] || []).slice(0, 5),
    }));

    return { data: { users }, error: null };
  },

  async setSubscriptionStatus(targetUserId, subscriptionMode, planId = 'monthly') {
    const mode = String(subscriptionMode || '').toLowerCase();
    const pid = String(planId || 'monthly').toLowerCase();
    if (!targetUserId || (mode !== 'premium' && mode !== 'free')) {
      return { data: null, error: { message: 'Invalid subscription request.' } };
    }

    const plan = PLAN_MAP[pid] || PLAN_MAP.monthly;
    const now = new Date();
    const periodEnd = new Date(now);
    if (plan.months === null) periodEnd.setFullYear(periodEnd.getFullYear() + 100);
    else periodEnd.setMonth(periodEnd.getMonth() + plan.months);

    if (mode === 'free') {
      const { error: cErr } = await supabase
        .from('stripe_subscriptions')
        .update({
          status: 'canceled',
          cancel_at_period_end: true,
          canceled_at: now.toISOString(),
          updated_at: now.toISOString(),
        })
        .eq('user_id', targetUserId)
        .in('status', ['active', 'trialing']);

      if (cErr) return { data: null, error: cErr };

      const { error: pErr } = await supabase
        .from('user_profiles')
        .update({
          subscription_status: 'free',
          subscription_plan: null,
          subscription_start_date: null,
          subscription_renewal_date: null,
          updated_at: now.toISOString(),
        })
        .eq('id', targetUserId);

      if (pErr) return { data: null, error: pErr };
    } else {
      const syntheticId = `admin_grant_${targetUserId}`;
      const { error: uErr } = await supabase.from('stripe_subscriptions').upsert(
        {
          user_id: targetUserId,
          stripe_subscription_id: syntheticId,
          stripe_customer_id: 'admin_customer',
          status: 'active',
          plan_id: pid,
          plan_name: plan.label,
          amount: plan.amount,
          currency: 'INR',
          interval: plan.months === null ? 'lifetime' : pid === 'yearly' ? 'year' : 'month',
          current_period_start: now.toISOString(),
          current_period_end: periodEnd.toISOString(),
          cancel_at_period_end: false,
          metadata: { provider: 'admin_manual' },
          updated_at: now.toISOString(),
        },
        { onConflict: 'stripe_subscription_id' }
      );

      if (uErr) return { data: null, error: uErr };

      const { error: pErr } = await supabase
        .from('user_profiles')
        .update({
          subscription_status: 'premium',
          subscription_plan: pid,
          subscription_start_date: now.toISOString(),
          subscription_renewal_date: periodEnd.toISOString(),
          updated_at: now.toISOString(),
        })
        .eq('id', targetUserId);

      if (pErr) return { data: null, error: pErr };
    }

    return { data: { ok: true }, error: null };
  },
};

export default adminSubscriptionService;
